import React, { useEffect, useMemo, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCPrintContainer, RCCServicesApi, RCCSwitch, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import Loader from "src/components/Loader";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import List from "src/components/List";
import { useAuth } from "src/context/Auth/context";
import { IndicatorLevel } from "src/components/ModalIndicator/interface";
import { IRefPrintContainer } from "react-common-components/dist/components/PrintContainer/interface";
import { IMetba003 } from "../Components/Objectives/interface";
import { IMetba002 } from "../Components/Perspective/interface";
import { IMetba001001 } from "../interface";
import BlockContainer, { BlueSpan, BoxInfo, CenterContainer, PageCover, PrintStyles, WrapTex } from "./style";

export interface Itens{
    item1: string;
    item2: string;
}

const MapsPrint: React.FC = () => {
	const params = useParams<{
        mapId: string;
    }>();

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { company } = useAuth();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
    
	const [loading, setLoading] = useState(false);
	const [errorLoading, setErrorLoading] = useState(false);
	const [mapSelected, setMapSelected] = useState<IMetba001001>({} as IMetba001001);

	const [showIniciative, setShowIniciative] = useState(false);
	const [showIndicator, setShowIndicator] = useState(false);
	const [showProject, setShowProject] = useState(false);

	const { 
		register : registerPrint
	} = useForm();


	// Print
	const printRef = useRef<IRefPrintContainer>(null);
	const executePrint = () => {
		printRef.current?.print();
	};

	async function getMap(idMap: number){
		setLoading(true);
		try{
			const result = await RCCServicesApi.getRequest({
				method: "Maps/SelectMapPerspectiveObjective",
				urlParameters: {id: idMap}
			});

			setMapSelected(result.data);
			setLoading(false);

		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar detalhes do mapa"), addToast);
			setLoading(false);
			setErrorLoading(true);
		}
	}

	const colProjectName = (row: any) => (
		<>
			<span>
				{row.pra005Pra002Nome}
			</span>
		</>
	);

	const colProgramName = (row: any) => (
		<>
			<span>
				{row.pra005Pra001Nome}
			</span>
		</>
	);

	const colIndicatorName = (row: any) => (
		<>
			<span>
				{row.mea004Iqc001Nome}
			</span>
		</>
	);

	const colIndicatorLevel = (row: any) => (
		<>
			<span>
				{getLabelIndicatorLevel(row.mea004Iqc001Nivel)}
			</span>
		</>
	);

	const getLabelIndicatorLevel = (level: IndicatorLevel) => {
		switch (level) {
		case IndicatorLevel.OPERATIONAL:
			return translate("Operacional");
		case IndicatorLevel.STRATEGIC:
			return translate("Estratégico");
		case IndicatorLevel.TATIC:
			return translate("Tático");
		default:
			return "";
		}
	};

	const renderInfoMap = useMemo(() => (
		<>
			<RCCPrintContainer
				ref={printRef}
				styleInline={PrintStyles}
				documentTitle={`${translate("Livro do Plano Estratégico")}-${company.efa002Fantasia}-${mapSelected.mea001Nome}`}
			>
				<PageCover className="cover">
					<CenterContainer>
						{company.efa002Imagem && (
							<img src={`${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_empresas/${company.efa002Imagem}`} alt="Company Logo"/>
						)}
						{/* <img src="http://effective-01.s3.amazonaws.com/score_des/imagens_empresas/2ed0b601073b13b89fb25606b0a41613.png" alt="Home Page" />			 */}
						<BoxInfo>
							<div><h1><strong>{translate("Livro do Plano Estratégico")}</strong></h1></div>
							<div><h2><i>{company.efa002Fantasia}</i></h2></div>
							<div><WrapTex>{mapSelected.mea001Nome}</WrapTex></div>
						</BoxInfo>
					</CenterContainer>
				</PageCover>
				<RCCGrid 
					container
					justifyContent="center"
					className="noPrintElement"
					styleInline="padding: 10px"
				>
					<BlueSpan><h2>{mapSelected.mea001Nome}</h2></BlueSpan>   
					<hr/>
				</RCCGrid>
				<RCCGrid container>
					<RCCGrid container>
						<BlueSpan><h3>{translate("Dados Gerais")}</h3></BlueSpan>
					</RCCGrid>
					<BlockContainer>
						<RCCGrid container>
							<strong>{`${translate("Descrição")}:`}</strong>&nbsp;
							<span>{ mapSelected.mea001Descricao}</span>
						</RCCGrid>
						<RCCGrid container>
							<strong>{translate("Visão")}:</strong>&nbsp;
							<span>{mapSelected.mea001Visao}</span>
						</RCCGrid>
						<RCCGrid container>
							<strong>{translate("Missão")}:</strong>&nbsp;
							<span>{mapSelected.mea001Missao}</span>
						</RCCGrid>
						<RCCGrid container>
							<strong>{translate("Valores")}:</strong>&nbsp;
							<span>{mapSelected.mea001Valores}</span>
						</RCCGrid>
					</BlockContainer>
				</RCCGrid>
				<hr/>
				<RCCGrid container>
					{
						mapSelected.metba002?.map((perspective: IMetba002) => (
							<BlockContainer key={`perspective-${perspective.mea002Id}`}>
								<RCCGrid container>
									<span>{`${translate("Perspectiva")}:`}</span>&nbsp;
									<strong>{perspective.mea002Nome}</strong>
								</RCCGrid>
								<RCCGrid container>
									<small>{perspective.mea002Descricao}</small>
								</RCCGrid>
                        
								{/* OBJECTIVES */}
								{
									perspective.metba003 && perspective.metba003.length > 0 &&
									<BlockContainer>
										<RCCGrid container>
											<BlueSpan>{translate("Objetivo")}</BlueSpan>
										</RCCGrid>
										{
											perspective.metba003?.map((objective: IMetba003) => (
												<BlockContainer key={`objective-${objective.mea003Id}`}>
													<RCCGrid container>
														<li style={{listStyleType: "disc"}}>{objective.mea003Nome}</li>
													</RCCGrid>
													<RCCGrid container>
														<small>{objective.mea003Descricao}</small>
													</RCCGrid>

													{/* INICIATIVE */}
													{
														showIniciative && objective.intba001 && objective.intba001.length > 0 &&
														<BlockContainer>
															<RCCGrid container>
																<BlueSpan>{translate("Iniciativas")}</BlueSpan>
															</RCCGrid>
															<RCCGrid container styleInline="margin-left: 1.5rem">
																<ul style={{listStyleType: "circle"}}>
																	{
																		objective.intba001?.map((iniciative) => (
																			<li key={`iniciative-${iniciative.ina001Id}`}>
																				{iniciative.ina001Titulo}
																			</li>
																		))
																	}
																</ul>
															</RCCGrid>
														</BlockContainer>
													}
                                                
													{/* INDICATOR */}
													{
														showIndicator && objective.metba004 && objective.metba004.length > 0 &&
														<BlockContainer>
															<RCCGrid container>
																<BlueSpan>{translate("Indicadores")}</BlueSpan>
															</RCCGrid>
															<List 
																showHeader
																data={objective.metba004 || []}
																columns={[{
																	idColumn: "item1",
																	nameColumn: translate("Nome"),
																	xs: 12,
																	sm: 7,
																	justify: "left",
																	truncate: true,
																	cellRenderer: colIndicatorName
																},{
																	idColumn: "item2",
																	nameColumn: translate("Nível"),
																	xs: 12,
																	sm: 4,
																	justify: "left",
																	truncate: true,
																	cellRenderer: colIndicatorLevel
																}]}
																layout="table"
															/>
														</BlockContainer>
													}

													{/* PROJECT */}
													{
														showProject && objective.prtba005 && objective.prtba005.length > 0 &&
														<BlockContainer>
															<RCCGrid container>
																<BlueSpan>{translate("Projetos")}</BlueSpan>
															</RCCGrid>
															<List 
																styleInline="display flex"
																showHeader
																data={objective.prtba005 || []}
																columns={[{
																	idColumn: "item1",
																	nameColumn: translate("Nome"),
																	xs: 12,
																	sm: 7,
																	justify: "left",
																	truncate: true,
																	cellRenderer: colProjectName
																},{
																	idColumn: "item2",
																	nameColumn: translate("Programa"),
																	xs: 12,
																	sm: 4,
																	justify: "left",
																	truncate: true,
																	cellRenderer: colProgramName
																}]}
																layout="table"
															/>
														</BlockContainer>
													}
												</BlockContainer>
											))
										}
									</BlockContainer>
								}
								<hr/>
							</BlockContainer>
						))
					}
				</RCCGrid>
			</RCCPrintContainer>
		</>
	),[mapSelected, showIniciative, showIndicator, showProject]);

	useEffect(() => {
		getMap(Number(params.mapId));
	}, [params]);

	return (
		<>
			{loading ? (
				<Loader/>) : (
				<>
					{
						errorLoading ? (
							<RCCAlert 
								title={translate("Atenção")}
								description={translate("Erro ao informações")}
							/>
						) : (
							<>
								<RCCGrid 
									className="print"
									container
									justifyContent="space-between"
									alignContent="center"
								>
									<RCCGrid></RCCGrid>
									<RCCGrid
										container
										justifyContent="space-between" 
										styleInline="width: unset !important; display: flex"
									>
										<RCCSwitch styleInline="margin: 10px 10px"
											name="iniciative" 
											label={translate("Visualizar Iniciativas")} 
											register={registerPrint} 
											defaultChecked={showIniciative}
											onChange={() => {setShowIniciative(!showIniciative);}}
										/>
										<RCCSwitch styleInline="margin: 10px 10px"
											name="indicator" 
											label={translate("Visualizar Indicadores")} 
											register={registerPrint} 
											defaultChecked={showIndicator}
											onChange={() => {setShowIndicator(!showIndicator);}}
										/>
										<RCCSwitch styleInline="margin: 10px 10px"
											name="project" 
											label={translate("Visualizar Projetos")} 
											register={registerPrint} 
											defaultChecked={showProject}
											onChange={() => {setShowProject(!showProject);}}
										/>
									</RCCGrid>
									<RCCGrid>
										<RCCTooltip
											text={translate("Imprimir")}
											color="blue"
										>
											<RCCButton
												icon={{
													name: "print",
													color: "#FFF",
													size: 16}
												}
												color="blue"
												size="md"
												onClick={() => executePrint()}
											/>
										</RCCTooltip>
									</RCCGrid>
								</RCCGrid>
								<hr />

								{renderInfoMap}
							</>
						)
					}
				</>
			)
			}
		</>
	);
};

export default MapsPrint;
